import React, { useMemo, useState, useCallback } from "react";
import useSWR from "swr";

import AxiosClient from "../../../Network/AxiosClient";
import { useNotification } from "../../Notifications/NotificationProvider";
import { fetcher } from "../../../utilities/_fetcher";
import { ReasonColumn } from "../../MerchantLoanRequests/components/ReasonColumn";

const ShimmerSkeleton = () => (
  <div className="shimmer-skeleton">
    {[...Array(2)].map((_, colIndex) => (
      <div key={colIndex} className="shimmer-column">
        <div className="shimmer-header"></div>
        {[...Array(6)].map((_, rowIndex) => (
          <div key={rowIndex} className="shimmer-row"></div>
        ))}
      </div>
    ))}
  </div>
);

const ReasonsModal = ({ onClose }) => {
  const [columns, setColumns] = useState([]);
  const [original, setOriginal] = useState([]);
  const [saving, setSaving] = useState(false);
  const dispatchNotification = useNotification();

  // Fetch all QC reasons
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-reason`;

  const {
    data: reasonsData,
    error: reasonsError,
    mutate: mutateReasons,
  } = useSWR(apiUrl, fetcher);

  const loading = !reasonsData && !reasonsError;

  // Transform API data to columns
  React.useEffect(() => {
    if (reasonsData && Array.isArray(reasonsData)) {
      // Filter and transform the data
      const l1Reasons = reasonsData
        .filter(
          (reason) =>
            reason.user_type === "l1" && reason.reason_type === "reject"
        )
        .map((reason) => ({
          id: reason.reason_id.toString(),
          text: reason.reason,
          deactivated: !reason.is_active,
          isFromApi: true,
        }));

      const l2Reasons = reasonsData
        .filter(
          (reason) =>
            reason.user_type === "l2" && reason.reason_type === "reject"
        )
        .map((reason) => ({
          id: reason.reason_id.toString(),
          text: reason.reason,
          deactivated: !reason.is_active,
          isFromApi: true,
        }));

      const transformedColumns = [
        {
          id: "cs-l1-reject",
          title: "CS L1 Rejection Reasons",
          reasons: l1Reasons,
        },
        {
          id: "cs-l2-reject",
          title: "CS L2 Rejection Reasons",
          reasons: l2Reasons,
        },
      ];

      setColumns(transformedColumns);
      setOriginal(JSON.parse(JSON.stringify(transformedColumns)));
    }
  }, [reasonsData]);

  // Handle API errors
  React.useEffect(() => {
    if (reasonsError) {
      console.error("Failed to fetch bank inclusion reasons:", reasonsError);

      const errorMessage =
        reasonsError?.response?.data?.message ||
        reasonsError?.message ||
        "Failed to fetch bank inclusion reasons";

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });

      // Set empty columns on error
      setColumns([
        {
          id: "cs-l1-reject",
          title: "CS L1 Rejection Reasons",
          reasons: [],
        },
        {
          id: "cs-l2-reject",
          title: "CS L2 Rejection Reasons",
          reasons: [],
        },
      ]);
      setOriginal([]);
    }
  }, [reasonsError, dispatchNotification]);

  const generateModifications = useCallback(() => {
    const modifications = [];

    columns.forEach((column) => {
      const userType = column.id.includes("l1") ? "L1" : "L2";
      const originalReasons =
        original.find((c) => c.id === column.id)?.reasons || [];

      column.reasons.forEach((reason) => {
        const originalReason = originalReasons.find((r) => r.id === reason.id);

        if (!reason.isFromApi) {
          // New reason being added
          modifications.push({
            reason_id: null,
            reason: reason.text,
            command: "add",
            user_type: userType,
            reason_type: "reject",
          });
        } else if (originalReason) {
          // Existing reason being activated/deactivated
          if (originalReason.deactivated !== reason.deactivated) {
            modifications.push({
              reason_id: parseInt(reason.id),
              command: reason.deactivated ? "deactivate" : "activate",
            });
          }
        }
      });
    });

    return modifications;
  }, [columns, original]);

  const hasChanges = useMemo(
    () => JSON.stringify(columns) !== JSON.stringify(original),
    [columns, original]
  );

  const updateColumn = useCallback((id, reasons) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, reasons } : c))
    );
  }, []);

  const onCancel = useCallback(() => {
    onClose();
    setColumns(original);
  }, [onClose, original]);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      const modifications = generateModifications();

      if (modifications.length > 0) {
        await AxiosClient.post(
          `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-reason`,
          { modifications },
          {
            headers: {
              Authorization:
                "Bearer " + JSON.parse(localStorage.getItem("currUserToken")),
            },
          }
        );

        mutateReasons();

        dispatchNotification({
          type: "SUCCESS",
          message: "Bank inclusion reasons updated successfully",
        });
      } else {
        dispatchNotification({
          type: "INFO",
          message: "No changes to save",
        });
      }

      setOriginal(columns);
      onClose();
    } catch (error) {
      console.error("Failed to save bank inclusion reasons:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save changes. Please try again.";

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  }, [
    generateModifications,
    columns,
    onClose,
    mutateReasons,
    dispatchNotification,
  ]);

  return (
    <div className="reason-modal-overlay">
      <div className="reason-modal">
        <div className="reason-modal__content">
          {loading || columns.length === 0 ? (
            <ShimmerSkeleton />
          ) : (
            <div className="reasons-grid">
              {columns.map((c) => (
                <ReasonColumn
                  key={c.id}
                  column={c}
                  onChange={(r) => updateColumn(c.id, r)}
                />
              ))}
            </div>
          )}
        </div>
        <footer className="reason-modal__footer">
          <button
            className="reason-modal-btn reason-modal-btn__cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="reason-modal-btn reason-modal-btn__save"
            onClick={onSave}
            disabled={!hasChanges || saving || loading}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReasonsModal;
